import { Group } from '@components/layout';
import { Tag } from '@components/tag';
import { Tooltip } from '@components/tooltip';
import { getFormattedDateString } from '@components/typography';
import { IntComparator } from '@generated/graphql';
import { Stack, Typography, capitalize, useTheme } from '@mui/material';
import { DateFilterValue, DeckardFilter, Filter } from './types';

type FilterChipsProps<T> = {
  filter: DeckardFilter<T>;
  variant?: 'primary' | 'secondary';
  clearFilterKey: (key: string) => void;
  updateFilter: (key: string, newValue: unknown) => void;
};

export const FilterChips = <T,>({
  filter,
  variant,
  clearFilterKey,
  updateFilter,
}: FilterChipsProps<T>) => {
  return (
    <Group flexWrap="wrap">
      {Object.keys(filter)
        .filter((key) => {
          if (key === 'texts') {
            return false;
          }
          if ((filter[key] as Filter<unknown>).type === 'boolean') {
            return (filter[key] as Filter<boolean>).value;
          }
          if (Array.isArray((filter[key] as Filter<unknown>).value)) {
            return ((filter[key] as Filter<unknown[]>).value as []).length > 0;
          }
          return false;
        })
        .map((key) => {
          const filterItem = filter[key] as Filter<unknown>;
          const filterType = (filter[key] as Filter<unknown>).type;
          return filterType === 'date' ? (
            <DateFilterChip
              key={key}
              filter={filterItem as Filter<DateFilterValue[]>}
              variant={variant}
              onDelete={(newValue) => updateFilter('dates', newValue)}
            />
          ) : filterType === 'number' ? (
            <NumberFilterChip
              key={key}
              filter={filterItem}
              name={filterItem.label ?? capitalize(key)}
              variant={variant}
              onDelete={() => clearFilterKey(key)}
            />
          ) : filterType === 'numberRange' ? (
            <NumberRangeFilterChip
              key={key}
              filter={filterItem as Filter<{ gte?: number; lte?: number }[]>}
              name={filterItem.label ?? capitalize(key)}
              variant={variant}
              onDelete={() => clearFilterKey(key)}
            />
          ) : filterType === 'size' ? (
            <SizeFilterChip
              key={key}
              filter={filterItem}
              name={filterItem.label ?? capitalize(key)}
              variant={variant}
              onDelete={() => clearFilterKey(key)}
            />
          ) : filterType === 'text' ? (
            <TextFilterChip
              key={key}
              filter={filterItem as Filter<string[]>}
              name={filterItem.label ?? capitalize(key)}
              variant={variant}
              onDelete={() => clearFilterKey(key)}
            />
          ) : filterType === 'boolean' ? (
            <BooleanFilterChip
              label={filterItem.label ?? capitalize(key)}
              onDelete={() => clearFilterKey(key)}
              variant={variant}
            />
          ) : (
            <FilterChip
              key={key}
              filter={filterItem}
              name={filterItem.label ?? capitalize(key)}
              variant={variant}
              onDelete={() => clearFilterKey(key)}
            />
          );
        })}
    </Group>
  );
};

const NumberRangeFilterChip = ({
  filter,
  name,
  variant,
  onDelete,
}: {
  filter: Filter<{ gte?: number; lte?: number }[]>;
  name: string;
  variant?: 'primary' | 'secondary';
  onDelete: VoidFunction;
}) => {
  const { palette } = useTheme();
  const value = filter.value as { gte?: number; lte?: number }[];
  if (value.length === 0) {
    return null;
  }
  const minValue = value.find((v) => v.gte)?.gte;
  const maxValue = value.find((v) => v.lte)?.lte;
  const rangeLabel = minValue === maxValue ? `${minValue}` : `${minValue} - ${maxValue}`;
  return (
    <Tag
      label={`${name}: ${rangeLabel}`}
      onDelete={onDelete}
      sx={{
        backgroundColor:
          variant === 'primary' ? palette.background.paper : palette.input.background.default,
      }}
    />
  );
};

const BooleanFilterChip = ({
  label,
  onDelete,
  variant,
}: {
  label: string;
  onDelete: VoidFunction;
  variant?: 'primary' | 'secondary';
}) => {
  const { palette } = useTheme();
  return (
    <Tag
      label={label}
      onDelete={onDelete}
      sx={{
        backgroundColor:
          variant === 'primary' ? palette.background.paper : palette.input.background.default,
      }}
    />
  );
};

const DateFilterChip = ({
  filter,
  variant,
  onDelete,
}: {
  filter: Filter<DateFilterValue[]>;
  variant?: 'primary' | 'secondary';
  onDelete: (newValue: unknown) => void;
}) => {
  const { palette } = useTheme();
  const value = filter.value as DateFilterValue[];
  if (value.length === 0) {
    return null;
  }
  return (
    <>
      {value.map((val, idx) => (
        <Tag
          key={`${val?.gte ? `from-${getFormattedDateString({ date: val.gte.toISOString(), nullOverride: 'invalid' })}` : `to-${getFormattedDateString({ date: val.lte.toISOString(), nullOverride: 'invalid' })}`}`}
          label={`${val?.gte ? `From: ${getFormattedDateString({ date: val.gte.toISOString(), nullOverride: 'Invalid' })}` : `To: ${getFormattedDateString({ date: val.lte.toISOString(), nullOverride: 'Invalid' })}`}`}
          onDelete={() => onDelete(value.filter((_, i) => i !== idx))}
          sx={{
            backgroundColor:
              variant === 'primary' ? palette.background.paper : palette.input.background.default,
          }}
        />
      ))}
    </>
  );
};

const FilterChip = <T,>({
  filter,
  name,
  variant,
  onDelete,
}: {
  filter: Filter<T>;
  name: string;
  variant?: 'primary' | 'secondary';
  onDelete: VoidFunction;
}) => {
  const { palette } = useTheme();
  if (filter.type === 'boolean') {
    throw new Error('Boolean filter cannot be used in FilterChips');
  }
  const value = filter.value as T[];
  const { display } = filter;
  if (value.length > 1) {
    return (
      <Tooltip
        title={
          <Stack gap={1}>
            {display.map((disp) => (
              <Typography variant="caption2" key={disp}>
                {disp}
              </Typography>
            ))}
          </Stack>
        }
        placement="bottom"
        variant="secondary"
        PopperProps={{
          disablePortal: true,
          sx: {
            '& .MuiTooltip-tooltip': {
              backgroundColor: palette.background.paper,
              boxShadow: palette.shadow.modal,
            },
          },
        }}
      >
        <Tag
          label={`${name}: (${value.length})`}
          onDelete={onDelete}
          sx={{
            backgroundColor:
              variant === 'primary' ? palette.background.paper : palette.input.background.default,
          }}
        />
      </Tooltip>
    );
  }
  return (
    <Tag
      label={`${name}: ${filter.display[0]}`}
      onDelete={onDelete}
      sx={{
        backgroundColor:
          variant === 'primary' ? palette.background.paper : palette.input.background.default,
      }}
    />
  );
};

const NumberFilterChip = <T,>({
  filter,
  name,
  variant,
  onDelete,
}: {
  filter: Filter<T>;
  name: string;
  variant?: 'primary' | 'secondary';
  onDelete: VoidFunction;
}) => {
  const { palette } = useTheme();
  if (filter.type === 'boolean') {
    throw new Error('Boolean filter cannot be used in FilterChips');
  }
  const value = filter.value as IntComparator[];
  const key = Object.keys(value?.[0])?.[0];
  const symbol = key === 'lte' ? '≤' : key === 'gte' ? '≥' : '';
  return (
    <Tag
      label={`${name}: ${symbol} ${value?.[0]?.[key as keyof IntComparator]}`}
      onDelete={onDelete}
      sx={{
        backgroundColor:
          variant === 'primary' ? palette.background.paper : palette.input.background.default,
      }}
    />
  );
};

const SizeFilterChip = <T,>({
  filter,
  name,
  variant,
  onDelete,
}: {
  filter: Filter<T>;
  name: string;
  variant?: 'primary' | 'secondary';
  onDelete: VoidFunction;
}) => {
  if (filter.type === 'boolean') {
    throw new Error('Boolean filter cannot be used in SizeFilterChip');
  }
  const filterValue = filter.value as IntComparator[];
  const { palette } = useTheme();
  const key = Object.keys(filterValue?.[0])?.[0];
  const symbol = key === 'lte' ? '≤' : key === 'gte' ? '≥' : '';
  if (!filterValue || filterValue.length === 0) {
    return null;
  }
  const value =
    filterValue[0][key] /
    (filter.display[0] === 'KB' ? 1000 : filter.display[0] === 'MB' ? 1000000 : 1000000000);
  return (
    <Tag
      label={`${name}: ${symbol} ${value} ${filter.display[0]}`}
      onDelete={onDelete}
      sx={{
        backgroundColor:
          variant === 'primary' ? palette.background.paper : palette.input.background.default,
      }}
    />
  );
};

const TextFilterChip = ({
  filter,
  name,
  variant,
  onDelete,
}: {
  filter: Filter<string[]>;
  name: string;
  variant?: 'primary' | 'secondary';
  onDelete: VoidFunction;
}) => {
  const { palette } = useTheme();
  if (filter.type === 'boolean') {
    throw new Error('Boolean filter cannot be used in FilterChips');
  }
  const { value, display } = filter;
  if (value.length > 1) {
    return (
      <Tooltip
        title={
          <Stack gap={1}>
            {value.map((val, idx) => (
              <Typography variant="caption2" key={val}>
                {display[idx] ?? val}
              </Typography>
            ))}
          </Stack>
        }
        placement="bottom"
        variant="secondary"
        PopperProps={{
          disablePortal: true,
          sx: {
            '& .MuiTooltip-tooltip': {
              backgroundColor: palette.background.paper,
              boxShadow: palette.shadow.modal,
            },
          },
        }}
      >
        <Tag
          label={`${name}: (${value.length})`}
          onDelete={onDelete}
          sx={{
            backgroundColor:
              variant === 'primary' ? palette.background.paper : palette.input.background.default,
          }}
        />
      </Tooltip>
    );
  }
  return (
    <Tag
      label={`${name}: ${filter.display[0]}`}
      onDelete={onDelete}
      sx={{
        backgroundColor:
          variant === 'primary' ? palette.background.paper : palette.input.background.default,
      }}
    />
  );
};
